import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Package, Menu, Ban } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

export default function AdminProducts() {
  const { authAxios } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterMacro, setFilterMacro] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMacro, setBulkMacro] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    sku: "",
    unit: "kg",
    price_list: "",
    price_min: "",
    price_purchase: "",
    price_promo: "",
    price_auth: "",
    image_url: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        authAxios.get("/products"),
        authAxios.get("/categories"),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category_id: "",
      sku: "",
      unit: "kg",
      price_list: "",
      price_min: "",
      price_purchase: "",
      price_promo: "",
      price_auth: "",
      image_url: "",
    });
    setEditingProduct(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || "",
      category_id: product.category_id,
      sku: product.sku,
      unit: product.unit,
      price_list: product.price_list.toString(),
      price_min: product.price_min?.toString() || "",
      price_purchase: product.price_purchase?.toString() || "",
      price_promo: product.price_promo?.toString() || "",
      price_auth: product.price_auth?.toString() || "",
      image_url: product.image_url || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price_list: parseFloat(formData.price_list),
        price_min: parseFloat(formData.price_min) || 0,
        price_purchase: parseFloat(formData.price_purchase) || 0,
        price_promo: formData.price_promo ? parseFloat(formData.price_promo) : null,
        price_auth: formData.price_auth ? parseFloat(formData.price_auth) : null,
      };

      if (editingProduct) {
        await authAxios.put(`/products/${editingProduct.id}`, payload);
        toast.success("Prodotto aggiornato con successo");
      } else {
        await authAxios.post("/products", payload);
        toast.success("Prodotto creato con successo");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!productToDelete) return;
    try {
      await authAxios.delete(`/products/${productToDelete.id}`);
      toast.success("Prodotto eliminato");
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const MACRO_OPTIONS = [
    { value: "Prodotti Triolo", label: "Prodotti Triolo", color: "text-amber-400 bg-amber-500/10" },
    { value: "Salumi", label: "Salumi", color: "text-red-400 bg-red-500/10" },
    { value: "Formaggi", label: "Formaggi", color: "text-yellow-400 bg-yellow-500/10" },
    { value: "Olive, Sottoli, Pesti", label: "Olive, Sottoli, Pesti", color: "text-emerald-400 bg-emerald-500/10" },
    { value: "Prodotti Ittici", label: "Prodotti Ittici", color: "text-cyan-400 bg-cyan-500/10" },
    { value: "Frutta Secca e Aromi", label: "Frutta Secca e Aromi", color: "text-orange-400 bg-orange-500/10" },
    { value: "Altri", label: "Altri", color: "text-zinc-400 bg-zinc-500/10" },
  ];

  const TRIOLO_SUB_OPTIONS = ["Pasta Fresca", "Pasta Secca", "Prodotti da Forno"];

  const filteredProducts = useMemo(() => products.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat = filterCategory === "all" || p.category_id === filterCategory;
    const matchesMacro = filterMacro === "all" || p.macro_category === filterMacro;
    return matchesSearch && matchesCat && matchesMacro;
  }).sort((a, b) => (a.category_code || "").localeCompare(b.category_code || "")), [products, searchQuery, filterCategory, filterMacro]);

  // Memoized categories list filtered by count and sorted by code
  const categoriesForFilter = useMemo(
    () => categories.filter(c => c.product_count > 0).sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    [categories]
  );

  const quickChangeMacro = async (productId, newMacro) => {
    try {
      await authAxios.put(`/products/${productId}/macro`, { macro_category: newMacro });
      const updates = { macro_category: newMacro };
      if (newMacro !== "Prodotti Triolo") updates.triolo_sub = null;
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, ...updates } : p
      ));
      toast.success("Sezione aggiornata");
    } catch (error) {
      toast.error("Errore");
    }
  };

  const quickChangeTrioloSub = async (productId, newSub) => {
    try {
      await authAxios.put(`/products/${productId}`, { triolo_sub: newSub });
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, triolo_sub: newSub } : p
      ));
      toast.success("Sotto-categoria aggiornata");
    } catch (error) {
      toast.error("Errore");
    }
  };

  const toggleDisponibilita = async (productId) => {
    try {
      const res = await authAxios.put(`/products/${productId}/disponibilita`);
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, non_disponibile: res.data.non_disponibile } : p
      ));
      toast.success(res.data.non_disponibile ? "Prodotto non disponibile" : "Prodotto disponibile");
    } catch (error) {
      toast.error("Errore");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const applyBulkMacro = async () => {
    if (!bulkMacro || selectedIds.size === 0) return;
    try {
      await authAxios.put("/products/bulk-macro", {
        product_ids: Array.from(selectedIds),
        macro_category: bulkMacro,
      });
      setProducts(prev => prev.map(p =>
        selectedIds.has(p.id) ? { ...p, macro_category: bulkMacro } : p
      ));
      toast.success(`${selectedIds.size} prodotti spostati in "${bulkMacro}"`);
      setSelectedIds(new Set());
      setBulkMacro("");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">

      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Prodotti" />
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Prodotti</h1>
              <p className="text-zinc-400 text-sm sm:text-base">Gestisci il catalogo prodotti</p>
            </div>
          </div>
          <Button
            data-testid="add-product-btn"
            onClick={openCreateDialog}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Prodotto
          </Button>
        </div>

        {/* Search + Filters */}
        <div className="mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              data-testid="products-search"
              type="text"
              placeholder="Cerca per nome o SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger data-testid="filter-category-select" className="w-full sm:w-[200px] bg-[#18181b] border-white/10">
              <SelectValue placeholder="Tutte le categorie" />
            </SelectTrigger>
            <SelectContent className="bg-[#27272a] border-white/10 max-h-60">
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {categoriesForFilter.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.code ? `${cat.code} - ` : ""}{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterMacro} onValueChange={setFilterMacro}>
            <SelectTrigger data-testid="filter-macro-select" className="w-full sm:w-[200px] bg-[#18181b] border-white/10">
              <SelectValue placeholder="Tutte le sezioni" />
            </SelectTrigger>
            <SelectContent className="bg-[#27272a] border-white/10">
              <SelectItem value="all">Tutte le sezioni</SelectItem>
              {MACRO_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div data-testid="bulk-action-bar" className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-3 flex-wrap">
            <span className="text-amber-300 text-sm font-medium">{selectedIds.size} selezionati</span>
            <Select value={bulkMacro} onValueChange={setBulkMacro}>
              <SelectTrigger data-testid="bulk-macro-select" className="h-8 w-[200px] bg-[#18181b] border-amber-500/30 text-sm">
                <SelectValue placeholder="Sposta in sezione..." />
              </SelectTrigger>
              <SelectContent className="bg-[#27272a] border-white/10">
                {MACRO_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="bulk-apply-btn"
              size="sm"
              onClick={applyBulkMacro}
              disabled={!bulkMacro}
              className="bg-amber-500 hover:bg-amber-600 text-black text-sm"
            >
              Applica
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              className="text-zinc-400 text-sm"
            >
              Annulla
            </Button>
          </div>
        )}

        <div className="text-xs text-zinc-500 mb-2">{filteredProducts.length} / {products.length} prodotti</div>

        {/* Products Table */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">Nessun prodotto trovato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredProducts.length && filteredProducts.length > 0}
                      onChange={selectAllFiltered}
                      className="rounded border-white/20 accent-amber-500"
                    />
                  </TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Prodotto</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">SKU</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Cod.Cat.</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Categoria</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Marca</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Sezione Catalogo</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Sotto-cat.</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider text-center">Disp.</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider text-right">Listino</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow
                    key={product.id}
                    data-testid={`product-row-${product.id}`}
                    className={`border-white/5 ${selectedIds.has(product.id) ? 'bg-amber-500/5' : ''}`}
                  >
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(product.id)}
                        onChange={() => toggleSelect(product.id)}
                        className="rounded border-white/20 accent-amber-500"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#27272a] overflow-hidden flex-shrink-0">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-5 h-5 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-white text-sm">{product.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-zinc-400 text-xs">{product.sku}</TableCell>
                    <TableCell className="font-mono text-amber-400 text-xs">{product.category_code}</TableCell>
                    <TableCell className="text-zinc-400 text-xs">{product.category_name}</TableCell>
                    <TableCell className="text-zinc-400 text-xs">{product.brand_name}</TableCell>
                    <TableCell>
                      <Select
                        value={product.macro_category || "Altri"}
                        onValueChange={(val) => quickChangeMacro(product.id, val)}
                      >
                        <SelectTrigger
                          data-testid={`macro-select-${product.id}`}
                          className="h-7 w-[170px] bg-transparent border-white/5 hover:border-amber-500/30 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#27272a] border-white/10">
                          {MACRO_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {product.macro_category === "Prodotti Triolo" ? (
                        <Select
                          value={product.triolo_sub || "Pasta Fresca"}
                          onValueChange={(val) => quickChangeTrioloSub(product.id, val)}
                        >
                          <SelectTrigger
                            data-testid={`triolo-sub-select-${product.id}`}
                            className="h-7 w-[140px] bg-transparent border-white/5 hover:border-amber-500/30 text-xs"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27272a] border-white/10">
                            {TRIOLO_SUB_OPTIONS.map((sub) => (
                              <SelectItem key={sub} value={sub} className="text-xs">{sub}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-zinc-600">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        data-testid={`toggle-disp-${product.id}`}
                        onClick={() => toggleDisponibilita(product.id)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                          product.non_disponibile
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-emerald-500/10 text-emerald-400'
                        }`}
                        title={product.non_disponibile ? "Non disponibile - clicca per rendere disponibile" : "Disponibile - clicca per rendere non disponibile"}
                      >
                        {product.non_disponibile ? <Ban className="w-4 h-4" /> : <span className="text-xs font-bold">OK</span>}
                      </button>
                    </TableCell>
                    <TableCell className="text-right font-mono text-white text-sm">{product.price_list.toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" data-testid={`edit-product-${product.id}`} onClick={() => openEditDialog(product)} className="text-zinc-400 hover:text-white">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-product-${product.id}`} onClick={() => { setProductToDelete(product); setDeleteDialogOpen(true); }} className="text-zinc-400 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">
                {editingProduct ? "Modifica Prodotto" : "Nuovo Prodotto"}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {editingProduct
                  ? "Modifica i dettagli del prodotto"
                  : "Inserisci i dettagli del nuovo prodotto"}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Nome *</Label>
                  <Input
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-[#27272a] border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">SKU *</Label>
                  <Input
                    data-testid="product-sku-input"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    required
                    className="bg-[#27272a] border-white/10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Categoria *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(val) => setFormData({ ...formData, category_id: val })}
                  >
                    <SelectTrigger data-testid="product-category-select" className="bg-[#27272a] border-white/10">
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10 max-h-60">
                      {categoriesForFilter.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.code ? `${cat.code} - ` : ""}{cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Unita</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(val) => setFormData({ ...formData, unit: val })}
                  >
                    <SelectTrigger data-testid="product-unit-select" className="bg-[#27272a] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="lt">lt</SelectItem>
                      <SelectItem value="pz">pz</SelectItem>
                      <SelectItem value="conf">conf</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Descrizione</Label>
                <Textarea
                  data-testid="product-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#27272a] border-white/10 min-h-[80px]"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Prezzo Listino *</Label>
                  <Input
                    data-testid="product-price-list-input"
                    type="number"
                    step="0.01"
                    value={formData.price_list}
                    onChange={(e) => setFormData({ ...formData, price_list: e.target.value })}
                    required
                    className="bg-[#27272a] border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Prezzo Minimo</Label>
                  <Input
                    data-testid="product-price-min-input"
                    type="number"
                    step="0.01"
                    value={formData.price_min}
                    onChange={(e) => setFormData({ ...formData, price_min: e.target.value })}
                    className="bg-[#27272a] border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Prezzo Acquisto</Label>
                  <Input
                    data-testid="product-price-purchase-input"
                    type="number"
                    step="0.01"
                    value={formData.price_purchase}
                    onChange={(e) => setFormData({ ...formData, price_purchase: e.target.value })}
                    className="bg-[#27272a] border-white/10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-red-400">Prezzo Promo (visibile a tutti)</Label>
                  <Input
                    data-testid="product-price-promo-input"
                    type="number"
                    step="0.01"
                    value={formData.price_promo}
                    onChange={(e) => setFormData({ ...formData, price_promo: e.target.value })}
                    placeholder="Lascia vuoto se non in promo"
                    className="bg-[#27272a] border-red-500/30 focus:border-red-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-purple-400">Prezzo da Autorizzare (admin/agenti)</Label>
                  <Input
                    data-testid="product-price-auth-input"
                    type="number"
                    step="0.01"
                    value={formData.price_auth}
                    onChange={(e) => setFormData({ ...formData, price_auth: e.target.value })}
                    placeholder="Lascia vuoto se non richiesto"
                    className="bg-[#27272a] border-purple-500/30 focus:border-purple-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Immagine Prodotto</Label>
                <ImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  authAxios={authAxios}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-white/10"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  data-testid="product-submit-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  {editingProduct ? "Salva Modifiche" : "Crea Prodotto"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Conferma Eliminazione"
          description={`Sei sicuro di voler eliminare il prodotto "${productToDelete?.name}"? Questa azione non puo essere annullata.`}
          confirmLabel="Elimina"
          confirmClassName="bg-red-500 hover:bg-red-600 text-white"
          confirmTestId="confirm-delete-btn"
          onConfirm={handleDelete}
        />
      </main>
    </div>
  );
}
